import {
  ApolloClient,
  ApolloError,
  from,
  InMemoryCache,
  NormalizedCacheObject,
} from '@apollo/client';
import { RestLink } from 'apollo-link-rest'; //NOSONAR
import axios from 'axios';
import {
  Config,
  DownloadSavedReport,
  FormatPreviewRowType,
  InactivePopupAction,
  OptionType,
  PreviewRowType,
  RunReportDataFuncParams,
  FetchReportDataFuncParams,
  GetRunReportTotalRowsFuncParams,
  CheckExportJobStatusFuncParams,
  DownloadReportByParams,
  CheckExportDataAmountAndDownloadFuncParams,
  RunSavedReportDataFuncParams,
  AuditLogAction,
  ManuallyLoggedErrorType,
  DownloadDataPayload,
  DownloadRecentlyGeneratedReport,
  UserInfo,
  WeekDay,
  ScheduleType,
  GetDefaultScheduledReportValueFuncParams,
  ToggleSchedulingFuncParams,
  RunReportParamsType,
  RunReportDataType,
  FetchReportDataFuncResponse,
  FetchSnapshotDataFuncPayload,
  FetchSnapshotDataFuncParams,
  FetchSnapshotJobStatusFuncParams,
} from './Common.types';
import { RetryLink } from '@apollo/client/link/retry';
import { setContext } from '@apollo/client/link/context';
import {
  EXPORT_ERROR_MSG,
  LOCAL_STORAGE_IDLE_ACTION_ID,
  LOCAL_STORAGE_IDLE_HANDLER,
  LOCAL_STORAGE_INACTIVE_POPUP_ACTION,
  LOCAL_STORAGE_IS_INACTIVE_IN_ONE_TAB,
  MAX_POLLING_DURATION,
  NO_CONTENT_STRING,
  PENALTY_RETRY,
  POLLING_ERROR_MSG,
  POLLING_TIMEOUT_ERROR_MSG,
  POLLING_DELAY,
  STATUS_ERROR_RETRY,
  EXPORT_NO_DATA,
  APOLLO_NO_CACHE_POLICY,
  STORAGE_USER_INFO_PREFIX,
  STORAGE_OIDC_DATA_PREFIX,
  RUN_REPORT_PARAMS_LOCAL_STORAGE,
  DEFAULT_RUN_REPORT_LIMIT,
  DEFAULT_RUN_REPORT_OFFSET,
} from './Common.constants';
import { typePolicies } from './Common.variables';
import { detect } from 'detect-browser';
import Logger from '../../shared/logger/logger';
import { onError } from '@apollo/client/link/error';
import { GraphQLError } from 'graphql';
import { isObject } from 'lodash';
import { v4 as uuidv4 } from 'uuid'; //NOSONAR
import { FormikErrors, FormikProps, FormikTouched } from 'formik'; //NOSONAR
import {
  RunReportMutation,
  RunReportMutationVariables,
  RunReportDocument,
  FetchReportDataQuery,
  FetchReportDataQueryVariables,
  FetchReportDataDocument,
  GetRunReportJobStatusQuery,
  GetRunReportJobStatusQueryVariables,
  GetRunReportJobStatusDocument,
  ExportSavedReportQuery,
  ExportSavedReportQueryVariables,
  ExportSavedReportDocument,
  GetDownloadReportJobStatusDocument,
  GetDownloadReportJobStatusQuery,
  GetDownloadReportJobStatusQueryVariables,
  ExportReportDocument,
  ExportReportMutation,
  ExportReportMutationVariables,
  RunSavedReportDataQueryVariables,
  RunSavedReportDataQuery,
  RunSavedReportDataDocument,
  ExportRecentlyGeneratedReportQuery,
  ExportRecentlyGeneratedReportQueryVariables,
  ExportRecentlyGeneratedReportDocument,
  RunReportPayload,
  UserReportScheduleInput,
  FetchSnapshotDataDocument,
  FetchSnapshotDataJobStatusDocument,
  FetchSnapshotDataJobStatusQuery,
  FetchSnapshotDataJobStatusQueryVariables,
  FetchSnapshotDataMutation,
  FetchSnapshotDataMutationVariables,
  RunReportResponse,
} from '../../generated/graphql';
import {
  DEFAULT_MONTHLY_SCHEDULED_REPORT_VALUE,
  DEFAULT_SCHEDULED_REPORT_VALUE,
  DEFAULT_WEEKLY_SCHEDULED_REPORT_VALUE,
  INDEX_COL,
} from '../reportViewer/ReportViewer.constants';
import { SchedulingFormType } from '../reportViewer/ReportViewer.types';
import { toast } from 'react-toastify';
import { ROUTE_INFO } from '../route/Route.constants';
import { LogErrorParams } from '../../hooks/useLogAction';

const FIRST_CHAR_CODE = 65;
const MAX_ALPHABET_CHAR = 25;
const COUNT_DOWN_CONSTANT = 10;
const browser = detect();
export const clientOS = browser?.os ?? '';
export const clientBrowser = browser?.name ?? '';

export const mockedAxios = axios as jest.Mocked<typeof axios>;

interface LogErrorType {
  apiURL?: string;
  errorMessage: string;
  errorStack?: string;
}

export const buildApolloClient = (
  config: Config
): ApolloClient<NormalizedCacheObject> => {
  const restLink = new RestLink({
    uri: config.API_URL,
    responseTransformer: getResponseTransformer,
  });
  const totalRetryTime = config.RETRY_TIME + PENALTY_RETRY;
  const retryLink = new RetryLink({
    delay: {
      initial: config.RETRY_DELAY,
    },
    attempts: {
      max: totalRetryTime,
      retryIf: (error) => {
        if (
          error &&
          error.response &&
          error.response.status >= STATUS_ERROR_RETRY
        ) {
          return true;
        } else {
          return false;
        }
      },
    },
  });
  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, response }) => {
      const apiURL = config.API_URL;
      if (graphQLErrors) {
        if (Array.isArray(graphQLErrors)) {
          graphQLErrors.forEach((err: GraphQLError) => {
            logError({
              apiURL,
              errorMessage: err.message,
              errorStack: buildErrorDetails({
                errStack: err.stack,
                operation,
                response,
              }),
            });
          });
        } else {
          logError({
            apiURL,
            errorMessage: JSON.stringify(graphQLErrors),
            errorStack: buildErrorDetails({
              operation,
              response,
            }),
          });
        }
      }
      if (networkError) {
        logError({
          apiURL,
          errorMessage: networkError.message,
          errorStack: buildErrorDetails({
            errStack: networkError.stack,
            operation,
            response,
          }),
        });
      }
    }
  );

  const authLink = setContext((_, { headers }) => {
    const userInfo = getUserInfo();
    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${userInfo?.access_token}`,
      },
    };
  });

  return new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: typePolicies,
    }),
    link: from([errorLink, authLink, retryLink, restLink]),
  });
};

const getResponseTransformer: RestLink.ResponseTransformer = async (
  response
) => {
  try {
    if (response === null) {
      return null;
    }
    return response.json().then((data: any) => data);
  } catch (error) {
    return NO_CONTENT_STRING;
  }
};

export function getDateFormat(): string {
  const dateObject = new Intl.DateTimeFormat().formatToParts(new Date());
  return dateObject
    .map((object) => {
      switch (object.type) {
        case 'day':
          return 'dd';
        case 'month':
          return 'MM';
        case 'year':
          return 'yyyy';
        default:
          return '/';
      }
    })
    .join('');
}

export function isTrim(data: string): boolean {
  return !!data.trim();
}

export function checkRegexAlphanumberic(data: string): boolean {
  const regex = /^[0-9a-zA-Z\s-[(!?.,&/)\]'"]+$/;
  if (data.match(regex) || data === '') {
    return true;
  }
  return false;
}

export const sleep = (ms: number) =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function filterArrayByKeyword(
  arr: OptionType[],
  keyword: string
): OptionType[] {
  if (!keyword) {
    return arr;
  }
  return arr.filter((item: OptionType) => {
    const lowercaseItemName = item.displayName.toLowerCase();
    const lowercaseSearchKeyword = keyword.toLowerCase().trim();
    return lowercaseItemName.includes(lowercaseSearchKeyword);
  });
}

export function sortDataByAlphabetically<T>(
  dataSet: T[],
  keyGetter: (item: T) => string
): T[] {
  return [...dataSet].sort((a: any, b: any): number => {
    const valueOfA = keyGetter(a).toLowerCase();
    const valueOfB = keyGetter(b).toLowerCase();
    if (valueOfA < valueOfB) {
      return -1;
    }
    if (valueOfA > valueOfB) {
      return 1;
    }
    return 0;
  });
}

export function checkSelect<T>(
  checkValue: any | undefined,
  caseAccept: T,
  caseDeline: T
): T {
  if (checkValue) {
    return caseAccept;
  } else {
    return caseDeline;
  }
}

export const createQueryStringPath = (
  queryUrl: string,
  variables: any
): string => {
  const queryString = Object.keys(variables).reduce((acc, key): string => {
    if (variables[key] === null || variables[key] === undefined) {
      return acc;
    }
    const prefix = acc === '' ? '?' : `${acc}&`;
    if (Array.isArray(variables[key])) {
      const values = variables[key];
      const stackValues = values.map(
        (value: any) => `${key}[]=${encodeURIComponent(value.toString())}`
      );
      return `${prefix}${stackValues.join('&')}`;
    }
    return `${prefix}${key}=${encodeURIComponent(variables[key].toString())}`;
  }, '');
  return `${queryUrl}${queryString}`;
};

export function displayBorderOutline(
  isUserType: boolean | undefined = undefined
): string {
  let baseValue = 'w-full border';
  if (isUserType) {
    baseValue += ' border-parameter-border outline-none rounded';
  } else {
    baseValue += ' bg-white p-1px border-border-grey';
  }
  return baseValue;
}

export function formatString(str: string, ...val: string[]): string {
  for (let index = 0; index < val.length; index++) {
    str = str.replace(`{${index}}`, val[index]);
  }
  return str;
}

export function groupBy<T>(
  list: T[],
  keyGetter: (item: T) => string
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  list.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

export const getCharacterByIndex = (index: number): string => {
  let finalChar = '';
  if (index < 0) {
    finalChar = '';
  } else if (index <= MAX_ALPHABET_CHAR) {
    const charCode = index + FIRST_CHAR_CODE;
    finalChar = String.fromCharCode(charCode);
  } else {
    const indexForNextAlphabetSeries = index - MAX_ALPHABET_CHAR - 1;
    finalChar = `A${getCharacterByIndex(indexForNextAlphabetSeries)}`;
  }
  return finalChar;
};

export function arrangeArrayForVertialGrid<T>(
  arr: T[],
  colNumber: number
): T[] {
  if (arr.length / colNumber > 1) {
    const newArr: T[] = [];
    let arrIndex = 0;
    for (let i = 0; i < colNumber; i++) {
      let newArrIndex = i;
      while (newArrIndex < arr.length) {
        newArr[newArrIndex] = arr[arrIndex];
        newArrIndex = newArrIndex + colNumber;
        arrIndex++;
      }
    }
    return newArr;
  }
  return arr;
}

export const downloadReportByParams = async ({
  client,
  bodyParams,
  onNoData,
  onSuccess,
}: DownloadReportByParams): Promise<void> => {
  const { data } = await client.mutate<
    ExportReportMutation,
    ExportReportMutationVariables
  >({
    mutation: ExportReportDocument,
    variables: {
      input: {
        ...bodyParams,
        parameters: JSON.stringify(bodyParams.parameters),
      },
      bodyBuilder: getBodyBuilderForRunReport,
    },
  });
  const correlationId = data?.exportReport || '';
  await checkExportDataAmountAndDownload({
    client,
    correlationId,
    onSuccess,
    onNoData,
  });
};

export const getBodyBuilderForRunReport: any = (queryData: any): any => {
  const { input } = queryData.args;
  return {
    ...input,
    parameters: JSON.parse(input.parameters),
  };
};

export const downloadRecentlyGeneratedReport = async ({
  itemId,
  client,
  onSuccess,
}: DownloadRecentlyGeneratedReport): Promise<void> => {
  const { data } = await client.query<
    ExportRecentlyGeneratedReportQuery,
    ExportRecentlyGeneratedReportQueryVariables
  >({
    query: ExportRecentlyGeneratedReportDocument,
    variables: {
      input: {
        itemId,
      },
    },
    fetchPolicy: APOLLO_NO_CACHE_POLICY,
  });
  if (data?.exportRecentlyGeneratedReport?.fileUrl) {
    downloadFileWithUrl(data?.exportRecentlyGeneratedReport?.fileUrl);
    onSuccess();
  }
};

export const downloadFileWithUrl = (url: string): void => {
  const link = document.createElement('a');
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadSavedReport = async ({
  savedReportId,
  client,
  onNoData,
  onSuccess,
}: DownloadSavedReport): Promise<void> => {
  const { data } = await client.query<
    ExportSavedReportQuery,
    ExportSavedReportQueryVariables
  >({
    query: ExportSavedReportDocument,
    variables: {
      input: {
        savedReportId,
      },
    },
    fetchPolicy: APOLLO_NO_CACHE_POLICY,
  });
  const correlationId = data.exportSavedReport;
  await checkExportDataAmountAndDownload({
    client,
    correlationId,
    onSuccess,
    onNoData,
  });
};

export const checkExportDataAmountAndDownload = async ({
  client,
  correlationId,
  onNoData,
  onSuccess,
}: CheckExportDataAmountAndDownloadFuncParams): Promise<void> => {
  const { fileUrl } = await checkHasExportData({
    client,
    correlationId,
  });
  if (!fileUrl) {
    if (onNoData) {
      onNoData();
      return;
    } else {
      throw new Error(EXPORT_NO_DATA);
    }
  }
  onSuccess();
  downloadFileWithUrl(fileUrl);
};

export const checkHasExportData = async ({
  client,
  correlationId,
}: CheckExportJobStatusFuncParams): Promise<DownloadDataPayload> => {
  let downloadDataPayload: DownloadDataPayload | null = null;
  let pollingDuration = 0;
  while (downloadDataPayload === null) {
    if (pollingDuration === MAX_POLLING_DURATION) {
      throw new Error(POLLING_TIMEOUT_ERROR_MSG);
    }
    const { data } = await client.query<
      GetDownloadReportJobStatusQuery,
      GetDownloadReportJobStatusQueryVariables
    >({
      query: GetDownloadReportJobStatusDocument,
      variables: {
        correlationId,
      },
      fetchPolicy: APOLLO_NO_CACHE_POLICY,
    });
    if (data.downloadReportJobStatus === null) {
      downloadDataPayload = {
        fileUrl: null,
        fileName: null,
      };
    }
    const { fileUrl, completed, hasError, fileName } =
      data.downloadReportJobStatus;

    if (hasError) {
      throw new Error(EXPORT_ERROR_MSG);
    }

    if (completed) {
      downloadDataPayload = {
        fileUrl,
        fileName,
      };
      break;
    }
    await sleep(POLLING_DELAY);
    pollingDuration += POLLING_DELAY;
  }

  return downloadDataPayload;
};

export const fetchSnapshotData = async ({
  snapshotId,
  client,
}: FetchSnapshotDataFuncParams): Promise<FetchSnapshotDataFuncPayload | null> => {
  const { data } = await client.mutate<
    FetchSnapshotDataMutation,
    FetchSnapshotDataMutationVariables
  >({
    mutation: FetchSnapshotDataDocument,
    variables: { input: { snapshotId } },
  });
  const snapshotCorrelationId = data?.fetchSnapshotData;
  if (snapshotCorrelationId) {
    return fetchSnapshotJobStatus({
      snapshotCorrelationId,
      fetchingSnapshotId: snapshotId,
      client,
    });
  }
  return null;
};

const fetchSnapshotJobStatus = async ({
  snapshotCorrelationId,
  fetchingSnapshotId,
  client,
}: FetchSnapshotJobStatusFuncParams): Promise<FetchSnapshotDataFuncPayload | null> => {
  let fetchSnapshotDataPayload: FetchSnapshotDataFuncPayload | null = null;
  let pollingDuration = 0;
  while (fetchSnapshotDataPayload === null) {
    if (pollingDuration === MAX_POLLING_DURATION) {
      throw new Error(POLLING_TIMEOUT_ERROR_MSG);
    }
    const { data: fetchSnapshotData } = await client.query<
      FetchSnapshotDataJobStatusQuery,
      FetchSnapshotDataJobStatusQueryVariables
    >({
      query: FetchSnapshotDataJobStatusDocument,
      variables: {
        input: {
          snapshotId: fetchingSnapshotId,
          correlationId: snapshotCorrelationId,
        },
      },
      fetchPolicy: APOLLO_NO_CACHE_POLICY,
    });
    const {
      hasError,
      hasMore,
      data,
      maximumRows,
      completed,
      reportDefinitionName,
    } = fetchSnapshotData.fetchSnapshotDataJobStatus;

    if (hasError) {
      break;
    }

    if (completed) {
      fetchSnapshotDataPayload = {
        data,
        hasMoreData: hasMore,
        maximumRows,
        reportDefinitionName,
      };
      break;
    }
    await sleep(POLLING_DELAY);
    pollingDuration += POLLING_DELAY;
  }

  return fetchSnapshotDataPayload;
};
export const logError = ({
  apiURL,
  errorMessage,
  errorStack,
}: LogErrorType): void => {
  if (apiURL) {
    const logger = new Logger(apiURL);
    logger.logAction({
      action: AuditLogAction.LogError,
      message: errorMessage,
      detail: errorStack ?? '',
    });
  }
};

export const convertToStringOrUndefined = (value: any): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  return value.toString();
};

export const formatCountdownNumber = (val: number): string => {
  if (val < COUNT_DOWN_CONSTANT) {
    return `0${val}`;
  }
  return `${val}`;
};

export const removeInActivePopupActionStorage = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_INACTIVE_POPUP_ACTION);
};

export const setInActivePopupAction = (action: InactivePopupAction): void => {
  localStorage.setItem(LOCAL_STORAGE_INACTIVE_POPUP_ACTION, action);
};

export const removeIsInactiveInOneTab = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_IS_INACTIVE_IN_ONE_TAB);
};

export const setIsInactiveInOneTab = (): void => {
  localStorage.setItem(
    LOCAL_STORAGE_IS_INACTIVE_IN_ONE_TAB,
    JSON.stringify(true)
  );
};

export const checkIsInactiveInOneTab = (): boolean => {
  const isInactiveInOneTab = localStorage.getItem(
    LOCAL_STORAGE_IS_INACTIVE_IN_ONE_TAB
  );
  if (isInactiveInOneTab) {
    return true;
  }
  return false;
};

export const removeLocalStorageIdleHandler = (): void => {
  localStorage.removeItem(LOCAL_STORAGE_IDLE_HANDLER);
};

export const setupLocalStorageIdleActionId = (): string => {
  const newActionId = uuidv4();
  localStorage.setItem(LOCAL_STORAGE_IDLE_ACTION_ID, newActionId);
  return newActionId;
};

export const getLocalStorageIdleActionId = (): string | null => {
  return localStorage.getItem(LOCAL_STORAGE_IDLE_ACTION_ID);
};

export const formatPreviewData = (
  rowsData: PreviewRowType[]
): FormatPreviewRowType[] => {
  return rowsData.map((row) => {
    const isRowObj = isObject(row);
    if (isRowObj) {
      const formatObj: FormatPreviewRowType = {};
      Object.keys(row).forEach((rowKey) => {
        const rowValue = getKeyValue(rowKey, row);
        const isRowValueString = typeof rowValue === 'string';
        const isRowValueNotDefined =
          rowValue === null || rowValue === undefined;
        formatObj[rowKey] = isRowValueString
          ? rowValue
          : isRowValueNotDefined
          ? ''
          : JSON.stringify(rowValue);
      });
      return formatObj;
    } else {
      return row;
    }
  });
};

const getKeyValue = (key: string, obj: Record<string, any>): any => obj[key];
export function getErrorFieldForm<T>(
  form: FormikProps<T>,
  propertyName: keyof T
): string {
  const touched = form.touched[propertyName];
  const error = form.errors[propertyName];
  return touched && error ? (error as string) : '';
}

export function getErrorFieldArrayForm<T, A>(
  form: FormikProps<T>,
  propertyName: keyof T,
  propertyNameOfItem: keyof A,
  index: number
): string {
  const touched =
    form.touched[propertyName] &&
    (form.touched[propertyName] as FormikTouched<A>[])[index]
      ? (form.touched[propertyName] as FormikTouched<A>[])[index]
      : undefined;

  const error =
    form.errors[propertyName] &&
    (form.errors[propertyName] as FormikErrors<A>[])[index]
      ? (form.errors[propertyName] as FormikErrors<A>[])[index]
      : undefined;
  return touched &&
    error &&
    touched[propertyNameOfItem] &&
    error[propertyNameOfItem]
    ? (error[propertyNameOfItem] as string)
    : '';
}

export const runReportWithFilterData = async ({
  client,
  runReportParams,
  offset,
  limit,
}: RunReportDataFuncParams): Promise<RunReportPayload> => {
  const { data } = await client.mutate<
    RunReportMutation,
    RunReportMutationVariables
  >({
    mutation: RunReportDocument,
    variables: {
      input: {
        ...runReportParams,
        parameters: JSON.stringify(runReportParams.parameters),
        offset,
        limit,
      },
      bodyBuilder: getBodyBuilderForRunReport,
    },
  });
  return {
    reportCorrelationId: data?.runReport.reportCorrelationId ?? '',
    exportFileCorrelationId: data?.runReport.exportFileCorrelationId ?? '',
  };
};

export const capitalize = (data: string): string => {
  return data.charAt(0).toUpperCase() + data.slice(1);
};

export const runSavedReport = async ({
  client,
  offset,
  limit,
  savedReportId,
}: RunSavedReportDataFuncParams): Promise<RunReportResponse> => {
  const { data } = await client.query<
    RunSavedReportDataQuery,
    RunSavedReportDataQueryVariables
  >({
    query: RunSavedReportDataDocument,
    variables: {
      input: {
        offset,
        limit,
        savedReportId,
      },
    },
    fetchPolicy: APOLLO_NO_CACHE_POLICY,
  });
  return data?.runSavedReportData;
};

export const fetchReportData = async ({
  client,
  page,
  correlationId,
  shouldPolling,
}: FetchReportDataFuncParams): Promise<FetchReportDataFuncResponse> => {
  if (shouldPolling) {
    await getRunReportTotalRows({ client, correlationId });
  }
  const { data } = await client.query<
    FetchReportDataQuery,
    FetchReportDataQueryVariables
  >({
    query: FetchReportDataDocument,
    variables: {
      correlationId,
      page,
    },
    fetchPolicy: APOLLO_NO_CACHE_POLICY,
  });
  const returnedReportData = data?.fetchReportData;

  if (returnedReportData && returnedReportData !== NO_CONTENT_STRING) {
    const parsedReportDataArr = JSON.parse(returnedReportData);
    return {
      isCorrelationIdExpired: false,
      reportData: parsedReportDataArr ?? [],
    };
  }
  if (returnedReportData === NO_CONTENT_STRING) {
    return {
      isCorrelationIdExpired: true,
      reportData: [],
    };
  }
  return { isCorrelationIdExpired: false, reportData: [] };
};

export const getRunReportTotalRows = async ({
  client,
  correlationId,
}: GetRunReportTotalRowsFuncParams): Promise<number> => {
  let pollingDuration = 0;
  let rowsAmount: null | number = null;
  while (rowsAmount === null) {
    if (pollingDuration === MAX_POLLING_DURATION) {
      throw new Error(POLLING_TIMEOUT_ERROR_MSG);
    }
    const { data } = await client.query<
      GetRunReportJobStatusQuery,
      GetRunReportJobStatusQueryVariables
    >({
      query: GetRunReportJobStatusDocument,
      variables: {
        correlationId,
      },
      fetchPolicy: APOLLO_NO_CACHE_POLICY,
    });

    if (data.runReportJobStatus === null) {
      rowsAmount = 0;
      break;
    }

    const { completed, hasError, totalRows } = data.runReportJobStatus;
    if (hasError) {
      throw new Error(POLLING_ERROR_MSG);
    }
    if (completed) {
      rowsAmount = totalRows;
      break;
    }
    await sleep(POLLING_DELAY);
    pollingDuration += POLLING_DELAY;
  }
  return rowsAmount || 0;
};

export function removeItemFromArray<T>(array: T[], index: number): T[] {
  const removedArray = [...array];
  removedArray.splice(index, 1);
  return removedArray;
}

export function enumToOptionTypes(enumme: any): OptionType[] {
  const keys = Object.keys(enumme);
  return keys.map((key: string) => new OptionType(enumme[key], key));
}

export const preventSubmitFormByEnter = (e: any): void => {
  if (e.key === 'Enter') {
    e.preventDefault();
  }
};

export const getDataRowId = (row: any): string | number => {
  return row[INDEX_COL];
};

export const buildErrorDetails = (
  params: { [key: string]: any } | undefined | ManuallyLoggedErrorType
): string => {
  return JSON.stringify(params);
};

export const isOptionAll = (optionName: string): boolean => {
  const regexp = new RegExp(/^(\[All|All |All$)/i);
  return regexp.test(optionName);
};

export function sortByNumber<T>(
  dataSet: T[],
  keyGetter: (item: T) => number
): T[] {
  return [...dataSet].sort((a: any, b: any): number => {
    const valueOfA = keyGetter(a);
    const valueOfB = keyGetter(b);
    if (valueOfA >= valueOfB) {
      return 1;
    }
    return -1;
  });
}

const storageToSaveUserInfo = sessionStorage;

export function clearUserInfo(): void {
  for (let i = 0; i < storageToSaveUserInfo.length; i++) {
    const key = storageToSaveUserInfo.key(i);
    if (key && key.startsWith(STORAGE_USER_INFO_PREFIX)) {
      storageToSaveUserInfo.removeItem(key);
    }
  }
}
export function getUserInfo(): UserInfo | null {
  for (let i = 0; i < storageToSaveUserInfo.length; i++) {
    const key = storageToSaveUserInfo.key(i);
    if (key && key.startsWith(STORAGE_USER_INFO_PREFIX)) {
      const userInfo = storageToSaveUserInfo.getItem(key);
      if (userInfo) {
        return JSON.parse(userInfo);
      }
    }
  }
  return null;
}

export function getUserRoles(): string[] {
  const oidcUser = getUserInfo();
  return (oidcUser?.profile?.ExtRole as string)?.split(',') ?? [];
}

export function clearOidcData(): void {
  for (let i = 0; i < localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (
      key &&
      key.startsWith(STORAGE_OIDC_DATA_PREFIX) &&
      !key.startsWith(STORAGE_USER_INFO_PREFIX)
    ) {
      localStorage.removeItem(key);
    }
  }
}
export const getWeekDayByNumber = (num: number): string => {
  switch (num) {
    case 0:
      return WeekDay.sunday;
    case 1:
      return WeekDay.monday;
    case 2:
      return WeekDay.tuesday;
    case 3:
      return WeekDay.wednesday;
    case 4:
      return WeekDay.thursday;
    case 5:
      return WeekDay.friday;
    case 6:
      return WeekDay.saturday;
    default:
      return '';
  }
};

export const formatSchedulingForm = (
  schedulingFormValues: SchedulingFormType | undefined,
  monthlyTabName: string
): UserReportScheduleInput | null => {
  if (
    schedulingFormValues &&
    schedulingFormValues?.userReportSchedule?.isRecurringReport
  ) {
    const userReportSchedule = {
      ...schedulingFormValues.userReportSchedule,
    };
    if (schedulingFormValues.selectedTab === monthlyTabName) {
      if (schedulingFormValues.userReportSchedule.radioNormal) {
        delete userReportSchedule.specialDay;
      } else {
        delete userReportSchedule.dayOfMonth;
      }
      delete userReportSchedule.radioNormal;
    }
    return userReportSchedule as any;
  }
  return null;
};

export const getDefaultScheduledReportValue = ({
  selectedTab,
  monthlyTabName,
  weeklyTabName,
  defaultFormValues,
}: GetDefaultScheduledReportValueFuncParams): any => {
  let defaultScheduledReportValue: any = DEFAULT_SCHEDULED_REPORT_VALUE;
  const populatedDefaultFormValues: any =
    defaultFormValues?.userReportSchedule ?? {};
  const isDefaultValuesForWeeklyTab =
    defaultFormValues?.userReportSchedule?.scheduleType === ScheduleType.Weekly;
  if (selectedTab === weeklyTabName) {
    defaultScheduledReportValue = {
      ...defaultScheduledReportValue,
      ...DEFAULT_WEEKLY_SCHEDULED_REPORT_VALUE,
      daysOfWeek: [getWeekDayByNumber(new Date().getDay())],
    };
    if (isDefaultValuesForWeeklyTab) {
      defaultScheduledReportValue = {
        ...defaultScheduledReportValue,
        ...populatedDefaultFormValues,
      };
    }
  } else if (selectedTab === monthlyTabName) {
    defaultScheduledReportValue = {
      ...defaultScheduledReportValue,
      ...DEFAULT_MONTHLY_SCHEDULED_REPORT_VALUE,
    };
    if (!isDefaultValuesForWeeklyTab) {
      defaultScheduledReportValue = {
        ...defaultScheduledReportValue,
        ...populatedDefaultFormValues,
      };
    }
  }
  return defaultScheduledReportValue;
};

export const toggleScheduling = ({
  monthlyTabName,
  weeklyTabName,
  checked,
  defaultFormValues,
  setSelectedTab,
}: ToggleSchedulingFuncParams): void => {
  if (checked) {
    const scheduleType = defaultFormValues?.userReportSchedule?.scheduleType;
    if (!scheduleType) {
      setSelectedTab(weeklyTabName);
      return;
    }
    const isDefaultValuesForWeeklyTab =
      defaultFormValues?.userReportSchedule?.scheduleType ===
      ScheduleType.Weekly;
    if (isDefaultValuesForWeeklyTab) {
      setSelectedTab(weeklyTabName);
    } else {
      setSelectedTab(monthlyTabName);
    }
  }
};

export const buildDefaultSchedulingForm = (
  defaultFormValues: SchedulingFormType | undefined
): any => {
  return {
    name: defaultFormValues?.name ?? '',
    description: defaultFormValues?.description ?? '',
    userReportSchedule:
      defaultFormValues?.userReportSchedule ?? DEFAULT_SCHEDULED_REPORT_VALUE,
  };
};

export const getGraphqlErrorInfo = (
  error: ApolloError
): { statusCode?: number; result?: any } => {
  if (!error) {
    return {};
  }
  const { networkError } = error;
  if (!networkError) {
    return {};
  }
  const { statusCode, result } = networkError as any;
  return { statusCode, result };
};

export const runAdHocReport = async (
  client: ApolloClient<any>,
  runReportParams: RunReportParamsType,
  reportName: string,
  logError: (params: LogErrorParams) => void,
  handleWhenNoData?: () => void,
  handleUnexpectedError?: () => void
): Promise<void> => {
  try {
    const { reportCorrelationId, exportFileCorrelationId } =
      await runReportWithFilterData({
        client,
        runReportParams,
        offset: DEFAULT_RUN_REPORT_OFFSET,
        limit: DEFAULT_RUN_REPORT_LIMIT,
      });
    const totalRows = await getRunReportTotalRows({
      client,
      correlationId: reportCorrelationId,
    });
    if (totalRows === 0) {
      if (handleWhenNoData) {
        handleWhenNoData();
      }
    } else {
      const runReportData: RunReportDataType = {
        reportName,
        runReportParams,
        correlationId: reportCorrelationId,
        exportFileCorrelationId,
      };
      localStorage.setItem(
        RUN_REPORT_PARAMS_LOCAL_STORAGE,
        JSON.stringify(runReportData)
      );
      window.open(ROUTE_INFO.runReport.path, '_blank');
    }
  } catch (err: any) {
    logError({
      err,
      errDetails: {
        functionName: 'runAdHocReport',
      },
    });
    if (err.message === POLLING_TIMEOUT_ERROR_MSG) {
      toast.error(err.message);
    } else {
      if (handleUnexpectedError) {
        handleUnexpectedError();
      }
    }
  }
};

export const runUserReport = async (
  client: ApolloClient<any>,
  savedReportId: number,
  logError: (params: LogErrorParams) => void,
  handleWhenNoData?: () => void,
  handleUnexpectedError?: () => void
): Promise<void> => {
  try {
    const runReportResponse = await runSavedReport({
      client,
      offset: DEFAULT_RUN_REPORT_OFFSET,
      limit: DEFAULT_RUN_REPORT_LIMIT,
      savedReportId,
    });
    if (runReportResponse) {
      const totalRows = await getRunReportTotalRows({
        client,
        correlationId: runReportResponse.reportCorrelationId,
      });
      if (totalRows === 0) {
        if (handleWhenNoData) {
          handleWhenNoData();
        }
      } else {
        window.open(
          ROUTE_INFO.runSavedReport.path
            .replace(':id', savedReportId.toString())
            .replace(':correlationId', runReportResponse.reportCorrelationId)
            .replace(
              ':exportFileCorrelationId',
              runReportResponse.exportFileCorrelationId
            ),
          '_blank'
        );
      }
    }
  } catch (err: any) {
    logError({
      err,
      errDetails: {
        functionName: 'runUserReport',
      },
    });
    if (handleUnexpectedError) {
      handleUnexpectedError();
    } else {
      toast.error(err.message);
    }
  }
};
